import { Component, ElementRef, HostListener, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Params } from '@angular/router';
import { Subscription } from 'rxjs';
import { AutocompleteSettings } from 'src/app/shared/models/autocomplete-settings.model';
import { UtilitiesService } from 'src/app/shared/services/utilities.service';
import { UtilityFunctions } from 'src/app/shared/utilities/functions.models';

import { AppService } from '../../../app.service';
import { GridColumn, GridColumnTypeEnum } from '../../../models/common/grid-column.models';
import { OrderListItem } from '../../../models/common/order-list-item.model';
import { PhotographerRejectedShot } from '../../../models/photographer/photographer-rejected-shot.model';
import { PhotographerService } from '../../../services/photographer.service';
import { SidenavService } from '../../../shared/services/sidenav.service';
import { TopbarService } from '../../../shared/services/topbar.service';

@Component({
  standalone: false,
  selector: 'bt-photographer-dashboard',
  templateUrl: './photographer-dashboard.component.html',
  styleUrls: ['./photographer-dashboard.component.scss']
})

export class PhotographerDashboardComponent implements OnInit, OnDestroy {

  @ViewChild('replaceShot') public replaceShot: ElementRef;

  public months = ['Gen', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  public dayNowDate = new Date().getDate();
  public monthNowDate = this.months[new Date().getMonth()];
  public userName: string;
  public orders: { data?: OrderListItem[], page?: number, search?: string, size: number, total?: number } = {
    search: '',
    size: 10
  };
  public autocompleteOrders: { data?: OrderListItem[], page?: number, search?: string, total?: number, settings: AutocompleteSettings, selected?: OrderListItem, loaderId: string } = {
    search: '',
    loaderId: UtilityFunctions.newGuid(),
    settings: new AutocompleteSettings({
      textProperty: 'orderNumber',
      valueProperty: 'orderId',
      serverPagination: true,
      paginationSize: 50
    })
  };
  public rejectedShots: { data?: PhotographerRejectedShot[], dataToView?: PhotographerRejectedShot[], pageNumber?: number, pageSize: number, selected?: PhotographerRejectedShot } = {
    pageSize: 5
  };
  public gridColumnTypeEnum = GridColumnTypeEnum;
  public gridColumns: GridColumn[];
  public inUploadingShots: boolean;
  public highlightDropArea = false;
  public firstLoaderId = UtilityFunctions.newGuid();
  public secondLoaderId = UtilityFunctions.newGuid();

  private subscription = new Subscription();

  constructor(
    private appService: AppService,
    private sidenavService: SidenavService,
    private photoService: PhotographerService,
    private topbarService: TopbarService,
    private utilitiesService: UtilitiesService
  ) {
    this.userName = this.appService.user.userFirstName;
    this.setGridColumns();
    this.searchTextSubscription();
  }

  @HostListener('window:dragover', ['$event'])
  public windowDragover(ev: any) {
    this.onDragOver(ev, false);
  }

  @HostListener('window:drop', ['$event'])
  public windowDrop(ev: any) {
    ev.preventDefault();
    ev.stopPropagation();
    this.highlightDropArea = false;
  }

  public ngOnInit() {
    this.getOngoingOrders(0);
    this.getRejectedShots();
  }

  public ngOnDestroy() {
    this.subscription.unsubscribe();
  }

  public setRejectedShotsToView(page: number) {
    this.rejectedShots.pageNumber = page;
    const start = this.rejectedShots.pageNumber * this.rejectedShots.pageSize;
    const dataToView = this.rejectedShots.data.slice(start, start + this.rejectedShots.pageSize);
    !dataToView.length && this.rejectedShots.pageNumber !== 0 ?
      this.setRejectedShotsToView(this.rejectedShots.pageNumber - 1) : this.rejectedShots.dataToView = dataToView;
  }

  public onReplaceShot(item: PhotographerRejectedShot) {
    item.showMenu = false;
    this.rejectedShots.selected = item;
    this.replaceShot.nativeElement.click();
  }

  public onOrderInfo(item: PhotographerRejectedShot, modelId?: number) {
    const params: Params = { orderId: item.orderId, orderNumber: item.orderNumber };
    if (modelId) { params.modelId = modelId };
    this.sidenavService.newItemUrlPart('order-list', params);
  }

  public onSelectOrderToUploadShots(item: OrderListItem) {
    this.autocompleteOrders.selected = item;
  }

  public onReplaceShotUpload(event: any) {
    const files: FileList = event.target.files;
    this.photoService.replaceShot(files, this.rejectedShots.selected.shotId, this.secondLoaderId)
      .subscribe(() => this.getRejectedShots());
  }

  public onImagesUpload(event: any) {
    const files: FileList = event.target.files;
    this.uploadShots(files);
  }

  public onDragOver(ev: any, highlightArea: boolean) {
    ev.stopPropagation();
    ev.preventDefault();
    this.highlightDropArea = highlightArea;
  }

  public onDrop(event: any) {
    event.preventDefault();
    event.stopPropagation();
    if (this.autocompleteOrders.selected && !this.inUploadingShots) {
      const files: FileList = event.dataTransfer.files;
      if (files && files.length) { this.uploadShots(files); }
    }
  }

  public getOngoingOrders(page: number) {
    this.orders.page = page;
    // this.photoService.getOrders(page, this.orders.size, this.orders.search, true, null, this.firstLoaderId).subscribe(data => {
    //   this.orders.data = data.orders;
    //   this.orders.total = data.total;
    // });
  }

  public onOpenOrdersAutocomplete() {
    if (!this.autocompleteOrders.data) this.getAutocompleteOrders(0);
  }

  public getAutocompleteOrders(page: number) {
    this.autocompleteOrders.page = page;
    const size = this.autocompleteOrders.settings.paginationSize;
    // this.photoService.getOrders(page, size, this.autocompleteOrders.search, true, null, this.autocompleteOrders.loaderId).subscribe(data => {
    //   this.autocompleteOrders.data = !page ? data.orders : this.autocompleteOrders.data.concat(data.orders);
    // });
  }

  public onOrdersAutocompleteFilterChange(search: string) {
    this.autocompleteOrders.search = search ? search.toLowerCase() : '';
    this.getAutocompleteOrders(0);
  }

  private uploadShots(files: FileList) {
    this.inUploadingShots = true;
    this.photoService.uploadShot(files, this.autocompleteOrders.selected.orderId, this.autocompleteOrders.loaderId).subscribe(n => {
      if (n !== undefined) {
        this.inUploadingShots = false;
        this.highlightDropArea = false;
        this.utilitiesService.showSnackbar('Shots uploading successfully');
      }
    });
  }

  private searchTextSubscription() {
    this.subscription.add(this.topbarService.searchTextSubscribe.subscribe(data => {
      if (data !== this.orders.search) {
        this.orders.search = data;
        this.getOngoingOrders(0);
      }
    }));
  }

  private setGridColumns() {
    this.gridColumns = [
      { title: '', field: '', type: GridColumnTypeEnum.icon, width: 20 },
      { title: 'Order #', field: 'orderNumber' },
      { title: 'Status', field: 'orderStatusName' },
      { title: 'Due Date', field: 'dueDate', type: GridColumnTypeEnum.date }
    ];
  }

  private getRejectedShots() {
    this.photoService.getPhotographerRejectedShots(this.secondLoaderId).subscribe(data => {
      this.rejectedShots.data = data;
      this.setRejectedShotsToView(0);
    });
  }
}
